import React from 'react';
import { IUnit } from '@/types';
import UICollapsibleSection from '@/components/shared/UICollapsibleSection';
import './UnitInclusions.scss';

interface UnitInclusionsProps {
  unit: IUnit | null;
  collapsed: boolean;
  onToggleCollapse: () => void;
}

const baseClass = 'UnitInclusions';

// Допоміжний компонент для іконок "так/ні"
const Checkmark: React.FC<{ value: any }> = ({ value }) => (
  <i
    className={`fa-solid ${value ? 'fa-square-check' : 'fa-xmark'} ${
      baseClass
    }--${value ? 'checked' : 'nonchecked'}`}
  ></i>
);

// Допоміжний компонент для тегів з даними
const DetailTag: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className={`${baseClass}__detail-tag`}>{children}</div>
);

// Допоміжний компонент для елементів списку меблів
const FurnitureItem: React.FC<{
  label: string;
  item?: { included?: boolean; count?: number };
}> = ({ label, item }) => (
  <div className={`${baseClass}__furniture-item`}>
    <Checkmark value={item?.included} />
    <span>{label}</span>
    {item?.included && item.count !== undefined && (
      <DetailTag>{item.count}</DetailTag>
    )}
  </div>
);

const UnitInclusions: React.FC<UnitInclusionsProps> = ({
  unit,
  collapsed,
  onToggleCollapse,
}) => {
  const characteristics = unit?.characteristics;
  const furniture = characteristics?.furniture;
  const appliances = furniture?.appliances;

  const allAppliances = [
    { key: 'stove', label: 'Stove' },
    { key: 'microwave', label: 'Microwave oven' },
    { key: 'dishwasher', label: 'Dishwasher' },
    { key: 'refrigerator', label: 'Refrigerator' },
    { key: 'washer', label: 'Washer' },
    { key: 'dryer', label: 'Dryer' },
  ];

  // Створюємо конкретний тип для ключів меблів, щоб уникнути помилок TypeScript
  type FurnitureItemName =
    | 'tables'
    | 'chairs'
    | 'armchairs'
    | 'chests'
    | 'couches'
    | 'beds';

  const allFurniture: { key: FurnitureItemName; label: string }[] = [
    { key: 'tables', label: 'Table(s)' },
    { key: 'chairs', label: 'Chair(s)' },
    { key: 'armchairs', label: 'Armchair(s)' },
    { key: 'chests', label: 'Chest(s) of drawers' },
    { key: 'couches', label: 'Couch(es)' },
    { key: 'beds', label: 'Bed(s)' },
  ];

  return (
    <UICollapsibleSection
      title="Inclusions"
      description="Description of leased dwelling, accessories and dependencies."
      collapsed={collapsed}
      onToggleCollapse={onToggleCollapse}
      className={`${baseClass} mb-24`}
    >
      <div className={`${baseClass}__content-wrapper`}>
        {/* --- Parking --- */}
        <div className={`${baseClass}__section-pair`}>
          <div className={`${baseClass}__section-column`}>
            <div className={`${baseClass}__item-label`}>
              <Checkmark value={characteristics?.parking?.hasOutdoor} />
              <span>Outdoor parking</span>
            </div>
            {characteristics?.parking?.hasOutdoor && (
              <div className={`${baseClass}__detail-tags`}>
                <DetailTag>
                  {characteristics.parking.outdoorCount || 0} place(s)
                </DetailTag>
                {characteristics.parking.outdoorSpaces && (
                  <DetailTag>{characteristics.parking.outdoorSpaces}</DetailTag>
                )}
              </div>
            )}
          </div>
          <div className={`${baseClass}__section-column`}>
            <div className={`${baseClass}__item-label`}>
              <Checkmark value={characteristics?.parking?.hasIndoor} />
              <span>Indoor parking</span>
            </div>
            {characteristics?.parking?.hasIndoor && (
              <div className={`${baseClass}__detail-tags`}>
                <DetailTag>
                  {characteristics.parking.indoorCount || 0} place(s)
                </DetailTag>
                {characteristics.parking.indoorSpaces && (
                  <DetailTag>{characteristics.parking.indoorSpaces}</DetailTag>
                )}
              </div>
            )}
          </div>
        </div>

        {/* --- Storage & Other Accessories --- */}
        <div className={`${baseClass}__section-pair`}>
          <div className={`${baseClass}__section-column`}>
            <div className={`${baseClass}__item-label`}>
              <Checkmark value={characteristics?.storage?.hasLockerOrStorage} />
              <span>Locker or storage space</span>
            </div>
            {characteristics?.storage?.hasLockerOrStorage &&
              characteristics.storage.storageDescription && (
                <p className={`${baseClass}__description`}>
                  {characteristics.storage.storageDescription}
                </p>
              )}
          </div>
          <div className={`${baseClass}__section-column`}>
            <div className={`${baseClass}__item-label`}>
              <Checkmark
                value={characteristics?.otherAccessories?.hasOtherAccessories}
              />
              <span>Other accessories and dependencies</span>
            </div>
            {characteristics?.otherAccessories?.hasOtherAccessories &&
              characteristics.otherAccessories.otherAccessoriesDescription && (
                <p className={`${baseClass}__description`}>
                  {characteristics.otherAccessories.otherAccessoriesDescription}
                </p>
              )}
          </div>
        </div>

        {/* --- Furniture is Leased --- */}
        <div className={`${baseClass}__item-label`}>
          <Checkmark value={furniture?.isIncluded} />
          <span>Furniture is leased and included in the rent.</span>
        </div>

        {/* --- Appliances --- */}
        <div className={`${baseClass}__section`}>
          <h4 className={`${baseClass}__section-title`}>Appliances</h4>
          <div className={`${baseClass}__appliances-grid`}>
            {allAppliances.map(({ key, label }) => (
              <div className={`${baseClass}__item-label`} key={key}>
                <Checkmark
                  value={appliances?.[key as keyof typeof appliances]}
                />
                <span>{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* --- Furniture --- */}
        <div className={`${baseClass}__section`}>
          <h4 className={`${baseClass}__section-title`}>Furniture</h4>
          <div className={`${baseClass}__furniture-grid`}>
            <div className={`${baseClass}__furniture-column`}>
              {allFurniture.slice(0, 3).map(({ key, label }) => (
                <FurnitureItem
                  key={key}
                  label={label}
                  item={furniture?.[key]}
                />
              ))}
            </div>
            <div className={`${baseClass}__furniture-column`}>
              {allFurniture.slice(3).map(({ key, label }) => (
                <FurnitureItem
                  key={key}
                  label={label}
                  item={furniture?.[key]}
                />
              ))}
            </div>
          </div>
        </div>

        {/* --- Other Furniture --- */}
        <div className={`${baseClass}__section`}>
          <div className={`${baseClass}__item-label`}>
            <Checkmark value={furniture?.hasOther} />
            <h4 className={`${baseClass}__section-title`}>Other</h4>
          </div>
          {furniture?.hasOther && furniture.otherDescription && (
            <p className={`${baseClass}__description`}>
              {furniture.otherDescription}
            </p>
          )}
        </div>
      </div>
    </UICollapsibleSection>
  );
};

export default UnitInclusions;
